import { AuditedSerializable, ISerializable } from './base';
import { Preset, Subcategory } from './category';
import { Price, PriceDebug } from './price';
import { BoardStrength, BoardType } from './types';

export class BoxImage extends AuditedSerializable<BoxImage> {
  code: string | undefined;
  location: string | undefined;
  default: boolean | undefined;
  hover: string | undefined;

  override deserialize(input: any): BoxImage {
    this.location = input.location;
    this.default = input.default;
    this.hover = input.hover;
    super.deserialize(input);
    return this;
  }
}


export class BoxType extends AuditedSerializable<BoxType> {
  name: string | undefined;
  description: string | undefined;
  code: string | undefined;
  aeCode: string | undefined;
  boardTypeCode: string | undefined;
  widthScore: number | undefined;
  lengthScore: number | undefined;
  tuck: number | undefined;
  flap: number | undefined;
  minimumOrder: number | undefined;
  luxeMinimumOrder: number | undefined;
  maxQuantity: number | undefined;
  boardType: BoardType | undefined;
  subcategory: Subcategory | undefined;
  increment: number | undefined;
  defaultQuantity: number | undefined;

  override deserialize(input: any): BoxType {
    super.deserialize(input);
    this.name = input.name;
    this.description = input.description;
    this.code = input.code;
    this.aeCode = input.aeCode;
    this.boardTypeCode = input.boardTypeCode;
    this.widthScore = input.widthScore;
    this.lengthScore = input.lengthScore;
    this.tuck = input.tuck;
    this.flap = input.flap;
    this.minimumOrder = input.minimumOrder;
    this.luxeMinimumOrder = input.luxeMinimumOrder;
    this.maxQuantity = input.maxQuantity;
    this.increment = input.increment;
    this.defaultQuantity = input.defaultQuantity;
    if (input.boardType) {
      this.boardType = new BoardType().deserialize(input.boardType);
    }
    if (input.subcategory) {
      this.subcategory = new Subcategory().deserialize(input.subcategory);
    }
    return this;
  }
}

export class BoxPaperType implements ISerializable<BoxPaperType> {
  id: string | undefined;
  name: string | undefined;
  printTypes: BoxPrintType[] = [];
  finishes: string[] = [];
  secondary: string[] = [];

  get luxeAllowed() {
    return this.isAllowed(new BoxPrintType().deserialize({ id: 'luxe' }));
  }

  get glossAllowed() {
    return this.finishes.indexOf('Gloss') !== -1;
  }

  isAllowed(printType: BoxPrintType) {
    const pt = this.printTypes.find((value) => {
      return value.id === printType.id;
    });
    // console.log('printType', printType, pt);
    return pt !== undefined && pt.allowed;
  }

  deserialize(input: any): BoxPaperType {
    this.id = input.id;
    this.name = input.name;
    this.printTypes = input.printTypes || [];
    this.finishes = input.finishes || [];
    this.secondary = input.seconary || [];
    return this;
  }
}

export class BoxPrintType implements ISerializable<BoxPrintType> {
  id: string | undefined;
  name: string | undefined;
  allowed: boolean | undefined;

  deserialize(input: any): BoxPrintType {
    this.id = input.id;
    this.name = input.name;
    this.allowed = input.allowed;
    return this;
  }
}

export class Dimensions implements ISerializable<Dimensions> {
  length: number | undefined;
  width: number | undefined;
  depth: number | undefined;
  knockDownFlatLength: number | undefined;
  knockDownFlatWidth: number | undefined;
  knockDownFlatDepth: number | undefined;
  squareFeetTotal: number | undefined;
  blankSize: string | undefined;
  weight: number | undefined;

  deserialize(input: any): Dimensions {
    this.length = input.length;
    this.width = input.width;
    this.depth = input.depth;
    this.knockDownFlatLength = input.knockDownFlatLength;
    this.knockDownFlatWidth = input.knockDownFlatWidth;
    this.knockDownFlatDepth = input.knockDownFlatDepth;
    this.squareFeetTotal = input.squareFeetTotal;
    this.blankSize = input.blankSize;
    this.weight = input.weight;
    return this;
  }
}

export class Shipping implements ISerializable<Shipping> {
  length: number | undefined;
  width: number | undefined;
  depth: number | undefined;
  weight: number | undefined;

  deserialize(input: any): Shipping {
    this.length = input.length;
    this.width = input.width;
    this.depth = input.depth;
    this.weight = input.weight;
    return this;
  }
}

export class Violation implements ISerializable<Violation> {
  rule: string | undefined;
  message: string | undefined;
  warning: boolean | undefined;
  error: boolean | undefined;

  deserialize(input: any): Violation {
    this.rule = input.rule;
    this.message = input.message;
    this.warning = input.warning;
    this.error = input.error;
    return this;
  }
}

export class PricingResponse implements ISerializable<PricingResponse> {
  prices!: Price[];
  debug: PriceDebug | undefined;
  box!: Box;
  partition: any;
  presetMatches: Preset[] = [];
  customSizeIncrease: number = 0;
  rushMinimumQuantity: number = 0;
  rushMaximumQuantity: number = 0;
  defaultQuantity: number = 0;

  deserialize(input: any): PricingResponse {
    if (input.debug) {
      this.debug = new PriceDebug().deserialize(input.debug);
    }
    this.customSizeIncrease = input.customSizeIncrease;
    this.rushMinimumQuantity = input.rushMinimumQuantity;
    this.rushMaximumQuantity = input.rushMaximumQuantity;
    this.defaultQuantity = input.box.type.defaultQuantity
    this.prices = [];
    if (input.prices) {
      input.prices.forEach((value: any) => {
        if (value) {
          const price = new Price().deserialize(value);
          this.prices.push(price);
        }
      });
    }
    this.presetMatches = [];
    // Ryan Note - What does this do and why is it causing an allowcustomsizes error when a custom size is entered?
    if (input.presetMatches) {
      input.presetMatches.forEach((value: any) => {
        const preset = new Preset().deserialize(value);
        this.presetMatches.push(preset);
      });
    }
    if (input.box) {
      this.box = new Box().deserialize(input.box);
    }

    if (input.partition) {
      this.partition = input.partition;
    }
    return this;
  }
}

export class BoxMaterial extends AuditedSerializable<BoxMaterial> {
  code: string | undefined;
  paper: string | undefined;
  weightPerSquareFoot: number | undefined;
  boardStrength: BoardStrength | undefined;
  boardType: BoardType | undefined;
  premium: boolean | undefined;
  paperType: string | undefined;

  override deserialize(input: any): BoxMaterial {
    super.deserialize(input);
    this.code = input.code;
    this.paper = input.paper;
    this.weightPerSquareFoot = input.weightPerSquareFoot;
    if (input.boardStrength) {
      this.boardStrength = new BoardStrength().deserialize(input.boardStrength);
    }
    if (input.boardType) {
      this.boardType = new BoardType().deserialize(input.boardType);
    }
    this.premium = input.premium;
    this.paperType = input.paperType;
    return this;
  }
}

export class Box implements ISerializable<Box> {
  preset: boolean | undefined;
  boxType: BoxType | undefined;
  dimensions: Dimensions | undefined;
  shipping: Shipping | undefined;
  price: Price | undefined;
  material: BoxMaterial | undefined;
  violations!: Violation[];

  deserialize(input: any): Box {
    this.preset = input.preset;
    this.boxType = new BoxType().deserialize(input.type);
    this.dimensions = input.dimensions;
    this.shipping = input.shipping;
    if (input.price) {
      this.price = new Price().deserialize(input.price);
    }
    if (input.material) {
      this.material = new BoxMaterial().deserialize(input.material);
    }
    this.violations = [];
    if (input.constraintViolations) {
      input.constraintViolations.forEach((value: any) => {
        const violation = new Violation().deserialize(value);
        this.violations.push(violation);
      });
    }
    return this;
  }
}

export class ShippedBoxDescription {
  name: string | undefined;
  length: number | undefined;
  width: number | undefined;
  depth: number | undefined;
  weight: number | undefined;
  quantity: number | undefined;
}

export class BoxSelectablePreset {
  label!: string;
  code!: string;
  presets!: Preset[];

  static category(name: string, code: string) {
    const box = new BoxSelectablePreset();
    box.label = name.replace(/<sup>TM<\/sup>/i, '(TM)'); // TODO: do this in a better way
    box.code = code;
    box.presets = [];
    return box;
  }
}
export class Variant extends AuditedSerializable<Variant> {
  name: string | undefined;
  code: string | undefined;
  subcategory: Subcategory | undefined;

  override deserialize(input: any): Variant {
    super.deserialize(input);
    this.id = input.id;
    this.name = input.name;
    this.code = input.code;
    this.subcategory = input.subcategory;
    return this;
  }
}

export { BoardStrength, BoardType } from './types';

